/**
 * Background report (BackgroundReview): `extract` (BackgroundReportExtract) +
 * the per-section analyst layer (EXTRACT_VIEWER.md §6.1).
 */
import { enumTone } from "../format/registry";
import { sentenceCase } from "../format/labels";

const tag = (label, tone = "neutral") => ({ label, tone });

const backgroundConfig = {
  id: "background",
  schemas: ["BackgroundReview", "BackgroundExtractor"],
  routes: ["background"],
  title: "Background report",
  sections: [
    {
      kind: "fields",
      id: "summary",
      title: "Summary",
      fields: [
        { path: "backgroundReportSummary", label: "Summary" },
        { path: "background_report_issuer", label: "Issuer" },
        { path: "best_match_borrower.name", label: "Best match borrower", hideWhenEmpty: true },
      ],
    },
    {
      kind: "table",
      id: "report-sections",
      title: "Report sections",
      path: "sections",
      titleKey: "section_name",
      subtitleKey: "summary_of_section",
      columns: [],
      detail: ["raw_excerpt", "suggested_condition"],
      sortBy: { key: "risk_level", rank: ["critical", "high", "medium", "low", "none"] },
      rowMuted: (row) => row?.section_present === false,
      tags: (row) => {
        const out = [];
        if (row.risk_level) {
          const t = enumTone(row.risk_level);
          out.push(tag(`${sentenceCase(row.risk_level)} risk`, t === "ok" ? "ok" : t || "neutral"));
        }
        if (row.item_status) out.push(tag(sentenceCase(row.item_status), enumTone(row.item_status) || "neutral"));
        if (row.loe_required === true) out.push(tag("LOE required", "warn"));
        if (row.loe_subtype) out.push(tag(row.loe_subtype));
        if (typeof row.records_count === "number") {
          out.push(tag(`${row.records_count} ${row.records_count === 1 ? "record" : "records"}`));
        }
        if (row.pattern_of_activity === true) out.push(tag("Pattern of activity", "warn"));
        return out;
      },
    },
    {
      kind: "fields",
      id: "no-matches",
      title: "No matches",
      fields: [{ path: "sections_with_no_matches", label: "Sections checked with no records", itemTone: "ok" }],
    },
  ],
  detailPaths: ["best_match_borrower.id"],
  rules: [
    {
      id: "bg-high-risk",
      tone: "crit",
      when: { path: "sections", op: "anyRow", where: { path: "risk_level", op: "in", value: ["high", "critical"] } },
      title: "{count} {count|plural:section is,sections are} high risk or worse",
      target: { sectionId: "report-sections" },
    },
    {
      id: "bg-open",
      tone: "warn",
      when: { path: "sections", op: "anyRow", where: { path: "item_status", op: "eq", value: "open" } },
      title: "{count} open {count|plural:item,items}",
      target: { sectionId: "report-sections" },
    },
  ],
  checks: (p) => {
    const rows = Array.isArray(p?.sections) ? p.sections.filter((s) => s?.loe_required === true) : [];
    if (!rows.length) return [];
    const subtypes = [...new Set(rows.map((r) => r.loe_subtype).filter(Boolean))];
    return [
      {
        id: "bg-loe",
        tone: "warn",
        title: `${rows.length} ${rows.length === 1 ? "section needs" : "sections need"} an LOE`,
        detail: subtypes.length ? subtypes.join(" · ") : undefined,
        target: { sectionId: "report-sections" },
      },
    ];
  },
};

export default backgroundConfig;
